// In-browser softphone engine (v1) — a thin state machine over jssip.
//
// Owns exactly one JsSIP.UA (the user's own extension over SIP-WSS to the
// FreeSWITCH internal profile) and at most ONE live RTCSession. The page
// (pages/WebPhone.tsx) is a dumb renderer: it hands us a config from
// GET /api/webphone/config plus an <audio> element for the remote stream, and
// re-renders from the snapshots we emit. All jssip event wiring lives here so
// the UI never touches SIP.
//
// v1 scope: register, dial, receive/answer/decline, hangup, mute, DTMF.
// Deliberately NO transfer / hold / conference — keep the machine small.

import JsSIP from 'jssip';

export type SipConfig = {
  transport: string;
  ws_uri: string;
  sip_uri: string;
  domain: string;
  auth_user: string;
  password: string;
  display_name: string;
  register: boolean;
  stun_servers: string[];
  ice_servers: { urls: string | string[]; username?: string; credential?: string }[];
};

export type WebphoneConfigResponse = {
  ok: boolean;
  extension: string;
  domain_uuid: string;
  display_name: string;
  sip: SipConfig;
};

// Registration lifecycle of the UA (the "am I reachable" chip).
export type RegState = 'idle' | 'connecting' | 'registered' | 'unregistered' | 'failed';

// Lifecycle of the (single) call.
//   idle → dialing (we call) → ringing (180 heard) → in-call → ended → idle
//   idle → incoming (they call) → in-call → ended → idle
export type CallState = 'idle' | 'dialing' | 'ringing' | 'incoming' | 'in-call' | 'ended';

export type WebphoneSnapshot = {
  reg: RegState;
  call: CallState;
  /** Far-end identity — dialed digits or caller id. */
  remote: string;
  remoteName: string;
  muted: boolean;
  /** Epoch ms when the call was answered (for the duration ticker). */
  answeredAt: number | null;
  /** Last terminal cause ('Busy', 'Rejected', …) or registration error. */
  error: string | null;
};

type Listener = (s: WebphoneSnapshot) => void;

// jssip's RTCSession type isn't exported from the package root; the surface we
// use is tiny, so type it structurally rather than reaching into jssip/lib/*.
type Session = {
  connection: RTCPeerConnection | null;
  direction: 'incoming' | 'outgoing';
  remote_identity: { uri: { user: string }; display_name: string };
  isEnded(): boolean;
  answer(opts: unknown): void;
  terminate(opts?: unknown): void;
  sendDTMF(tone: string): void;
  on(ev: string, cb: (...args: any[]) => void): void;
};

const ENDED_LINGER_MS = 2500; // how long the "Call ended" state stays before idle

export class Webphone {
  private ua: JsSIP.UA | null = null;
  private session: Session | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private listener: Listener;
  private endedTimer: number | null = null;

  private snap: WebphoneSnapshot = {
    reg: 'idle', call: 'idle', remote: '', remoteName: '', muted: false,
    answeredAt: null, error: null,
  };

  constructor(listener: Listener) { this.listener = listener; }

  private emit(patch: Partial<WebphoneSnapshot>) {
    this.snap = { ...this.snap, ...patch };
    this.listener(this.snap);
  }

  get snapshot(): WebphoneSnapshot { return this.snap; }

  private iceServers: RTCIceServer[] = [];

  /** Boot the UA and register. Idempotent-ish: stops any previous UA first. */
  start(cfg: SipConfig, audioEl: HTMLAudioElement) {
    this.stop();
    this.audioEl = audioEl;
    this.iceServers = [
      ...(cfg.stun_servers || []).map((u) => ({ urls: u })),
      ...((cfg.ice_servers || []) as RTCIceServer[]),
    ];

    const socket = new JsSIP.WebSocketInterface(cfg.ws_uri);
    const ua = new JsSIP.UA({
      sockets: [socket],
      uri: cfg.sip_uri,
      password: cfg.password,
      display_name: cfg.display_name,
      register: cfg.register !== false,
      session_timers: false, // FS internal profile: avoid UPDATE-based timers the browser can't honor
    });
    this.ua = ua;

    ua.on('connecting', () => this.emit({ reg: 'connecting', error: null }));
    ua.on('connected', () => { /* WS up; wait for REGISTER result */ });
    ua.on('disconnected', () => this.emit({ reg: 'failed', error: 'WebSocket to PBX disconnected' }));
    ua.on('registered', () => this.emit({ reg: 'registered', error: null }));
    ua.on('unregistered', () => this.emit({ reg: 'unregistered' }));
    ua.on('registrationFailed', (e: { cause?: string }) =>
      this.emit({ reg: 'failed', error: `Registration failed${e?.cause ? `: ${e.cause}` : ''}` }));

    ua.on('newRTCSession', (data: { originator: string; session: unknown }) => {
      const session = data.session as Session;
      if (data.originator !== 'remote') return; // outgoing sessions are wired in dial()
      if (this.session && !this.session.isEnded()) {
        // v1 is single-line: politely 486 a second call instead of losing state.
        try { session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); } catch { /* already gone */ }
        return;
      }
      this.adoptSession(session);
      this.emit({
        call: 'incoming',
        remote: session.remote_identity?.uri?.user || 'unknown',
        remoteName: session.remote_identity?.display_name || '',
        error: null,
      });
    });

    ua.start();
  }

  /** Full teardown (page unmount / re-register with fresh config). */
  stop() {
    if (this.endedTimer !== null) { clearTimeout(this.endedTimer); this.endedTimer = null; }
    if (this.session && !this.session.isEnded()) { try { this.session.terminate(); } catch { /* noop */ } }
    this.session = null;
    if (this.ua) { try { this.ua.stop(); } catch { /* noop */ } this.ua = null; }
    this.emit({ reg: 'idle', call: 'idle', remote: '', remoteName: '', muted: false, answeredAt: null });
  }

  /** Place an outgoing call. No-op unless registered and idle. */
  dial(target: string) {
    const num = target.trim();
    if (!this.ua || !num || (this.snap.call !== 'idle' && this.snap.call !== 'ended')) return;
    this.clearEnded();
    try {
      const session = this.ua.call(num, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: this.iceServers },
      }) as unknown as Session;
      this.adoptSession(session);
      this.emit({ call: 'dialing', remote: num, remoteName: '', muted: false, error: null });
    } catch (e) {
      this.emit({ call: 'idle', error: e instanceof Error ? e.message : 'call failed' });
    }
  }

  /** Answer the ringing incoming call. */
  answer() {
    if (!this.session || this.snap.call !== 'incoming') return;
    this.session.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: this.iceServers },
    });
  }

  /** Decline an incoming call (486) or hang up a live/outgoing one. */
  hangup() {
    if (!this.session || this.session.isEnded()) return;
    try {
      if (this.snap.call === 'incoming') this.session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      else this.session.terminate();
    } catch { /* already terminated */ }
  }

  toggleMute() {
    const pc = this.session?.connection;
    if (!pc || this.snap.call !== 'in-call') return;
    const next = !this.snap.muted;
    for (const sender of pc.getSenders()) {
      if (sender.track && sender.track.kind === 'audio') sender.track.enabled = !next;
    }
    this.emit({ muted: next });
  }

  /** RFC 4733 DTMF during a live call. */
  sendDtmf(tone: string) {
    if (!this.session || this.snap.call !== 'in-call') return;
    if (!/^[0-9A-D#*]$/i.test(tone)) return;
    try { this.session.sendDTMF(tone); } catch { /* session raced to ended */ }
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private clearEnded() {
    if (this.endedTimer !== null) { clearTimeout(this.endedTimer); this.endedTimer = null; }
    if (this.snap.call === 'ended') this.emit({ call: 'idle', remote: '', remoteName: '', error: null });
  }

  private adoptSession(session: Session) {
    this.session = session;
    this.clearEnded();

    // Remote audio: the PC exists immediately for incoming (jssip builds it at
    // answer()) and is surfaced via 'peerconnection' for outgoing.
    const attach = (pc: RTCPeerConnection) => {
      pc.addEventListener('track', (e: RTCTrackEvent) => {
        if (this.audioEl && e.streams[0]) {
          this.audioEl.srcObject = e.streams[0];
          void this.audioEl.play().catch(() => { /* autoplay gate; user already clicked */ });
        }
      });
    };
    if (session.connection) attach(session.connection);
    session.on('peerconnection', (e: { peerconnection: RTCPeerConnection }) => attach(e.peerconnection));

    session.on('progress', () => {
      if (session.direction === 'outgoing') this.emit({ call: 'ringing' });
    });
    session.on('confirmed', () => this.emit({ call: 'in-call', answeredAt: Date.now(), muted: false }));
    session.on('accepted', () => {
      if (this.snap.call !== 'in-call') this.emit({ call: 'in-call', answeredAt: Date.now(), muted: false });
    });

    const onDone = (cause: string | null, failed: boolean) => {
      if (this.session !== session) return; // a newer call already took over
      this.session = null;
      if (this.audioEl) this.audioEl.srcObject = null;
      this.emit({
        call: 'ended', answeredAt: null, muted: false,
        error: failed && cause ? cause : null,
      });
      this.endedTimer = window.setTimeout(() => {
        this.endedTimer = null;
        if (this.snap.call === 'ended') this.emit({ call: 'idle', remote: '', remoteName: '', error: this.snap.error });
      }, ENDED_LINGER_MS);
    };
    session.on('ended', () => onDone(null, false));
    session.on('failed', (e: { cause?: string }) => onDone(e?.cause || 'Call failed', true));
  }
}
